import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { StepNavigation } from '../StepNavigation/StepNavigation';
import { LoadingSpinner } from '../ui/LoadingSpinner/LoadingSpinner';
import { ProcessingAnimation } from '../ui/ProcessingAnimation/ProcessingAnimation';
import { CheckIcon, XIcon } from '../Icons/Icons';
import { Pencil } from 'lucide-react';
import { useEvaluationStore } from '../../store/evaluationStore';
import { apiClient } from '../../api/client';
import type { RubricCell } from '../../types';
import { DifferenceExplanationDialog } from './RubricGeneration/DifferenceExplanationDialog';

interface DifferenceAnalysis {
  criterionIndex: number;
  criterionTitle: string;
  gradeBoundaryAnalyses: GradeBoundaryAnalysis[];
  isComplete: boolean;
}

interface GradeBoundaryAnalysis {
  upperGrade: string;
  lowerGrade: string;
  upperProducts: string[];
  lowerProducts: string[];
  differences: string[];
  isComplete: boolean;
}

export const RubricGeneration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { 
    products, 
    criteria, 
    setCurrentStep, 
    getEvaluationPosition, 
    getGrades,
    isLoading,
    setRubricData,
    getRubricData,
    setRubricAnalysis,
    getRubricAnalysis,
    saveDataAsync
  } = useEvaluationStore();

  // デバッグ用ログ
  console.log('RubricGeneration isLoading:', isLoading);

  // ストアのget関数も取得
  const get = useEvaluationStore.getState;

  const [analysisState, setAnalysisState] = React.useState<DifferenceAnalysis[]>([]);
  const [currentCriterionIndex, setCurrentCriterionIndex] = React.useState(0);
  const [currentBoundaryIndex, setCurrentBoundaryIndex] = React.useState(0);
  const [currentDifference, setCurrentDifference] = React.useState<string>('');
  const [rubric, setRubric] = React.useState<RubricCell[][]>([]);
  const [isAnalysisComplete, setIsAnalysisComplete] = React.useState(false);
  const [isRubricGenerated, setIsRubricGenerated] = React.useState(false);
  const [skipToAutoGenerate, setSkipToAutoGenerate] = React.useState(true); // デフォルトで自動生成モードに
  const [editingCell, setEditingCell] = React.useState<{row: number, col: number} | null>(null);
  const [editValue, setEditValue] = React.useState<string>('');
  const [showDifferenceDialog, setShowDifferenceDialog] = React.useState(false);
  const [differenceQuestions, setDifferenceQuestions] = React.useState<any[]>([]);
  const [differenceExplanations, setDifferenceExplanations] = React.useState<{questionId: string; explanation: string}[]>([]);

  // ストアからルーブリックデータを復元
  React.useEffect(() => {
    const existingRubric = getRubricData();
    console.log('Existing rubric data:', existingRubric);
    if (existingRubric && existingRubric.length > 0) {
      // 既存のルーブリックデータを直接使用（統一されたRubricCell型）
      setRubric(existingRubric);
      setIsRubricGenerated(true);
      setSkipToAutoGenerate(true);
      setIsAnalysisComplete(true);
      console.log('Rubric restored, setting isRubricGenerated to true');
    }
  }, [getRubricData]);

  // 各観点の等級境界分析を初期化または復元
  React.useEffect(() => {
    const initialAnalysis: DifferenceAnalysis[] = criteria.map((criterion, criterionIndex) => {
      const grades = getGrades(criterionIndex);
      const sortedGrades = [...grades].sort((a, b) => b.gradeXStart - a.gradeXStart);
      
      // ストアから既存の分析データを確認
      const existingAnalysis = getRubricAnalysis(criterionIndex);
      
      // 等級境界の分析を作成（A-B、B-C、C-D等）
      const gradeBoundaryAnalyses: GradeBoundaryAnalysis[] = [];
      for (let i = 0; i < sortedGrades.length - 1; i++) {
        const upperGrade = sortedGrades[i];
        const lowerGrade = sortedGrades[i + 1];
        
        // 各等級に属する作品を取得
        const upperProducts = products.filter((_, productIndex) => {
          const position = getEvaluationPosition(productIndex, criterionIndex);
          return position && position.x >= upperGrade.gradeXStart && 
                 (i === 0 || position.x < sortedGrades[i - 1].gradeXStart);
        }).map(p => p.reviewTitle);
        
        const lowerProducts = products.filter((_, productIndex) => {
          const position = getEvaluationPosition(productIndex, criterionIndex);
          return position && position.x >= lowerGrade.gradeXStart && position.x < upperGrade.gradeXStart;
        }).map(p => p.reviewTitle);
        
        if (upperProducts.length > 0 && lowerProducts.length > 0) {
          // 既存の分析データから差異を復元
          const existingBoundary = existingAnalysis?.gradeBoundaryAnalyses.find(
            b => b.upperGrade === upperGrade.gradeLetter && b.lowerGrade === lowerGrade.gradeLetter
          );
          
          gradeBoundaryAnalyses.push({
            upperGrade: upperGrade.gradeLetter,
            lowerGrade: lowerGrade.gradeLetter,
            upperProducts,
            lowerProducts,
            differences: existingBoundary?.differences || [],
            isComplete: (existingBoundary?.differences?.length || 0) > 0
          });
        }
      }
      
      return {
        criterionIndex,
        criterionTitle: criterion.title,
        gradeBoundaryAnalyses,
        isComplete: gradeBoundaryAnalyses.every(b => b.isComplete)
      };
    });
    
    setAnalysisState(initialAnalysis);
  }, [criteria, products, getGrades, getEvaluationPosition, getRubricAnalysis]);

  // ストアからルーブリックデータの復元
  React.useEffect(() => {
    const storedRubric = getRubricData();
    console.log('Checking stored rubric data:', storedRubric);
    if (storedRubric && storedRubric.length > 0) {
      // ストアから取得したルーブリックをそのまま使用（統一された型）
      setRubric(storedRubric);
      setIsRubricGenerated(true);
      setSkipToAutoGenerate(true); // 既存のルーブリックがある場合は初期選択をスキップ
      setIsAnalysisComplete(true);
      console.log('Rubric restored from store, isRubricGenerated set to true');
    } else {
      console.log('No rubric data in store');
    }
  }, [getRubricData]);

  // 分析完了状態をチェック
  React.useEffect(() => {
    const allComplete = analysisState.every(analysis => analysis.isComplete);
    setIsAnalysisComplete(allComplete);
    console.log('Analysis complete status:', allComplete);
  }, [analysisState]);

  // 現在の質問データを取得
  const getCurrentQuestion = () => {
    if (currentCriterionIndex >= analysisState.length) return null;
    
    const currentAnalysis = analysisState[currentCriterionIndex];
    if (currentBoundaryIndex >= currentAnalysis.gradeBoundaryAnalyses.length) return null;
    
    const currentBoundary = currentAnalysis.gradeBoundaryAnalyses[currentBoundaryIndex];
    
    return {
      analysis: currentAnalysis,
      boundary: currentBoundary,
      criterionTitle: currentAnalysis.criterionTitle,
      upperGrade: currentBoundary.upperGrade,
      lowerGrade: currentBoundary.lowerGrade,
      upperProducts: currentBoundary.upperProducts,
      lowerProducts: currentBoundary.lowerProducts
    };
  };

  const currentQuestion = getCurrentQuestion();

  // 差異回答の追加
  const handleAddDifference = () => {
    if (!currentDifference.trim() || !currentQuestion) return;

    setAnalysisState(prev => prev.map((analysis, analysisIndex) => 
      analysisIndex === currentCriterionIndex
        ? {
            ...analysis,
            gradeBoundaryAnalyses: analysis.gradeBoundaryAnalyses.map((boundary, boundaryIndex) =>
              boundaryIndex === currentBoundaryIndex
                ? { ...boundary, differences: [...boundary.differences, currentDifference.trim()] }
                : boundary
            )
          }
        : analysis
    ));
    
    setCurrentDifference('');
  };

  // 差異回答の削除
  const handleRemoveDifference = (differenceIndex: number) => {
    setAnalysisState(prev => prev.map((analysis, analysisIndex) => 
      analysisIndex === currentCriterionIndex
        ? {
            ...analysis,
            gradeBoundaryAnalyses: analysis.gradeBoundaryAnalyses.map((boundary, boundaryIndex) =>
              boundaryIndex === currentBoundaryIndex
                ? { ...boundary, differences: boundary.differences.filter((_, i) => i !== differenceIndex) }
                : boundary
            )
          }
        : analysis
    ));
  };

  // 境界分析の完了
  const handleCompleteBoundaryAnalysis = () => {
    if (!currentQuestion) return;

    setAnalysisState(prev => prev.map((analysis, analysisIndex) => 
      analysisIndex === currentCriterionIndex
        ? {
            ...analysis,
            gradeBoundaryAnalyses: analysis.gradeBoundaryAnalyses.map((boundary, boundaryIndex) =>
              boundaryIndex === currentBoundaryIndex
                ? { ...boundary, isComplete: true }
                : boundary
            )
          }
        : analysis
    ));

    // 次の境界に進む
    const currentAnalysis = analysisState[currentCriterionIndex];
    if (currentBoundaryIndex < currentAnalysis.gradeBoundaryAnalyses.length - 1) {
      setCurrentBoundaryIndex(prev => prev + 1);
    } else {
      // 現在の観点の分析を完了
      setAnalysisState(prev => prev.map((analysis, analysisIndex) => 
        analysisIndex === currentCriterionIndex
          ? { ...analysis, isComplete: true }
          : analysis
      ));
      
      // 次の観点に進む
      if (currentCriterionIndex < analysisState.length - 1) {
        setCurrentCriterionIndex(prev => prev + 1);
        setCurrentBoundaryIndex(0);
      } else {
        setIsAnalysisComplete(true);
      }
    }
  };

  // 等級差の質問をランダムに生成
  const generateDifferenceQuestions = () => {
    const questions: any[] = [];
    const maxQuestions = 3; // 2-3個の質問を生成
    
    // 各観点からランダムに評価された製品のペアを選択
    criteria.forEach((criterion, criterionIndex) => {
      if (questions.length >= maxQuestions) return;
      
      const grades = getGrades(criterionIndex);
      const sortedGrades = [...grades].sort((a, b) => b.gradeXStart - a.gradeXStart);
      
      // 各観点の製品と等級のマッピングを作成
      const productsByGrade: { [grade: string]: { title: string; index: number }[] } = {};
      
      products.forEach((product, productIndex) => {
        const position = getEvaluationPosition(productIndex, criterionIndex);
        if (position) {
          const grade = sortedGrades.find((g, i) => 
            position.x >= g.gradeXStart && 
            (i === 0 || position.x < sortedGrades[i - 1].gradeXStart)
          );
          if (grade) {
            if (!productsByGrade[grade.gradeLetter]) {
              productsByGrade[grade.gradeLetter] = [];
            }
            productsByGrade[grade.gradeLetter].push({
              title: product.reviewTitle,
              index: productIndex
            });
          }
        }
      });
      
      // 異なる等級のペアからランダムに選択
      const gradeLetters = Object.keys(productsByGrade);
      for (let i = 0; i < gradeLetters.length - 1 && questions.length < maxQuestions; i++) {
        const higherGrade = gradeLetters[i];
        const lowerGrade = gradeLetters[i + 1];
        
        const higherProducts = productsByGrade[higherGrade];
        const lowerProducts = productsByGrade[lowerGrade];
        
        if (higherProducts.length > 0 && lowerProducts.length > 0) {
          // ランダムに1つずつ選択
          const productA = higherProducts[Math.floor(Math.random() * higherProducts.length)];
          const productB = lowerProducts[Math.floor(Math.random() * lowerProducts.length)];
          
          questions.push({
            id: `${criterionIndex}-${productA.index}-${productB.index}`,
            criterionTitle: criterion.title,
            productA: productA.title,
            gradeA: higherGrade,
            productB: productB.title,
            gradeB: lowerGrade
          });
        }
      }
    });
    
    // 質問をシャッフルして最大数まで制限
    return questions.sort(() => Math.random() - 0.5).slice(0, Math.min(maxQuestions, questions.length));
  };

  // 差異説明付きでルーブリックを生成
  const handleShowDifferenceDialog = () => {
    const questions = generateDifferenceQuestions();
    if (questions.length > 0) {
      setDifferenceQuestions(questions);
      setShowDifferenceDialog(true);
    } else {
      // 質問が生成できない場合は直接生成
      handleGenerateRubric(true);
    }
  };

  // 差異説明ダイアログ完了時の処理
  const handleDifferenceDialogComplete = (explanations: { questionId: string; explanation: string }[]) => {
    setDifferenceExplanations(explanations);
    setShowDifferenceDialog(false);
    handleGenerateRubric(true, explanations);
  };

  // ルーブリック生成（差異分析をスキップして自動生成も可能）
  const handleGenerateRubric = async (autoGenerate: boolean = false, explanations: { questionId: string; explanation: string }[] = []) => {
    console.log('handleGenerateRubric called with autoGenerate:', autoGenerate);
    const set = useEvaluationStore.setState;
    set({ isLoading: true, error: null });
    
    // currentExaminationIdを取得
    const currentExaminationId = get().currentExaminationId;

    try {
      let analysisDataToUse = analysisState;
      
      // 自動生成の場合、空の差異分析データを作成
      if (autoGenerate) {
        analysisDataToUse = criteria.map((criterion, criterionIndex) => {
          const grades = getGrades(criterionIndex);
          const sortedGrades = [...grades].sort((a, b) => b.gradeXStart - a.gradeXStart);
          
          const gradeBoundaryAnalyses: GradeBoundaryAnalysis[] = [];
          for (let i = 0; i < sortedGrades.length - 1; i++) {
            const upperGrade = sortedGrades[i];
            const lowerGrade = sortedGrades[i + 1];
            
            const upperProducts = products.filter((_, productIndex) => {
              const position = getEvaluationPosition(productIndex, criterionIndex);
              return position && position.x >= upperGrade.gradeXStart && 
                     (i === 0 || position.x < sortedGrades[i - 1].gradeXStart);
            }).map(p => p.reviewTitle);
            
            const lowerProducts = products.filter((_, productIndex) => {
              const position = getEvaluationPosition(productIndex, criterionIndex);
              return position && position.x >= lowerGrade.gradeXStart && position.x < upperGrade.gradeXStart;
            }).map(p => p.reviewTitle);
            
            if (upperProducts.length > 0 && lowerProducts.length > 0) {
              gradeBoundaryAnalyses.push({
                upperGrade: upperGrade.gradeLetter,
                lowerGrade: lowerGrade.gradeLetter,
                upperProducts,
                lowerProducts,
                differences: [], // 自動生成時は空
                isComplete: true
              });
            }
          }
          
          return {
            criterionIndex,
            criterionTitle: criterion.title,
            gradeBoundaryAnalyses,
            isComplete: true
          };
        });
      }
      
      // 分析データを整理
      const analysisData = analysisDataToUse.map(analysis => ({
        criterion: analysis.criterionTitle,
        gradeBoundaryAnalyses: analysis.gradeBoundaryAnalyses.map(boundary => ({
          upperGrade: boundary.upperGrade,
          lowerGrade: boundary.lowerGrade,
          differences: boundary.differences
        })),
        grades: getGrades(analysis.criterionIndex)
      }));

      // 自動生成の場合は直接生成APIを使用
      let rubricData;
      if (autoGenerate) {
        // 差異説明を整形
        const formattedExplanations = explanations.map(exp => {
          const parts = exp.questionId.split('-');
          const criterionIndex = parseInt(parts[0]);
          const question = differenceQuestions.find(q => q.id === exp.questionId);
          
          return {
            explanationCriterionTitle: criteria[criterionIndex]?.title || '',
            explanationProductA: question?.productA || '',
            explanationGradeA: question?.gradeA || '',
            explanationProductB: question?.productB || '',
            explanationGradeB: question?.gradeB || '',
            explanation: exp.explanation
          };
        });

        rubricData = await apiClient.generateRubricDirect({
          directCriteria: criteria.map(c => ({
            criterionTitle: c.title,
            criterionDescription: c.description
          })),
          directProducts: products.map(p => ({ 
            title: p.reviewTitle, 
            detail: p.reviewComment 
          })),
          differenceExplanations: formattedExplanations.length > 0 ? formattedExplanations : undefined
        });
      } else {
        // 差異分析ベースの生成
        rubricData = await apiClient.generateRubric({
          examinationId: currentExaminationId || '',
          criteria: criteria.map(c => ({
            title: c.title,
            description: c.description
          })),
          products: products.map(p => ({ 
            title: p.reviewTitle, 
            detail: p.reviewComment 
          }))
        });
      }

      // ルーブリックデータをストアに保存
      setRubricData(rubricData.rubric);
      
      // 分析データもストアに保存
      analysisDataToUse.forEach((analysis, index) => {
        setRubricAnalysis(index, {
          criterionTitle: analysis.criterionTitle,
          gradeBoundaryAnalyses: analysis.gradeBoundaryAnalyses.map(boundary => ({
            upperGrade: boundary.upperGrade,
            lowerGrade: boundary.lowerGrade,
            differences: boundary.differences
          }))
        });
      });

      // APIレスポンスから取得したルーブリックデータをそのまま使用
      setRubric(rubricData.rubric);
      setIsRubricGenerated(true);
      if (autoGenerate) {
        setIsAnalysisComplete(true);
        setSkipToAutoGenerate(true);
      }

      // ルーブリックをデータベースに保存（評価プロジェクトIDが存在する場合）
      if (currentExaminationId && rubricData.rubric) {
        try {
          // analysisデータを収集
          const analysisDataArray = criteria.map((_, criterionIndex) => {
            const analysis = getRubricAnalysis(criterionIndex);
            return analysis ? {
              criterionIndex,
              data: JSON.stringify(analysis)
            } : null;
          }).filter(Boolean);
          
          await apiClient.saveRubric(currentExaminationId, rubricData.rubric, analysisDataArray);
          console.log('Rubric saved to database');
        } catch (saveError) {
          console.warn('Failed to save rubric to database:', saveError);
          // エラーは表示するが、処理は継続
        }
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : t('rubricGeneration.error.generation') });
    } finally {
      set({ isLoading: false });
    }
  };

  const handlePrev = async () => {
    await saveDataAsync();
    setCurrentStep(4); // 等級設定へ戻る
  };

  const handleNext = async () => {
    if (canProceed) {
      await saveDataAsync();
      setCurrentStep(6); // クイック評価へ進む
    }
  };

  const canProceed = isRubricGenerated;
  
  // デバッグログ
  React.useEffect(() => {
    console.log('RubricGeneration - canProceed:', canProceed, 'isRubricGenerated:', isRubricGenerated);
  }, [canProceed, isRubricGenerated]);

  if (products.length === 0 || criteria.length === 0) {
    return (
      <section className="section">
        <div className="empty-state">
          <h2 className="empty-state__title">{t('rubricGeneration.emptyState.title')}</h2>
          <p className="empty-state__description">
            {t('rubricGeneration.emptyState.description')}
          </p>
          <Button variant="secondary" onClick={handlePrev}>
            {t('rubricGeneration.emptyState.backButton')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="section__header">
        <h2 className="section__title">{t('rubricGeneration.title')}</h2>
        <p className="section__description">
          {t('rubricGeneration.description')}
        </p>
      </div>

      <div className="rubric-generation">
        {/* ローディングオーバーレイ */}
        {isLoading && (
          <ProcessingAnimation
            title={t('rubricGeneration.generation.generating')}
            description={t('rubricGeneration.generation.generatingDescription')}
            steps={[
              t('rubricGeneration.generation.step1'),
              t('rubricGeneration.generation.step2'),
              t('rubricGeneration.generation.step3')
            ]}
            currentStep={1}
          />
        )}
        
        {!isRubricGenerated ? (
          // 自動生成画面を直接表示
          <div className="generation-phase">
            <div className="rubric-generation-content">
              <div className="generation-action">
                <Button
                  onClick={handleShowDifferenceDialog}
                  disabled={isLoading}
                  variant="primary"
                  size="lg"
                  fullWidth
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      {t('rubricGeneration.generation.generating')}
                    </>
                  ) : (
                    t('rubricGeneration.generation.generateButton')
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : false ? (
          // 差異分析フェーズ（現在は使用しない）
          <div className="analysis-phase">
            {/* 観点タブ */}
            <div className="criterion-selector">
              <h3 className="criterion-selector__title">{t('rubricGeneration.criterionSelector.title')}</h3>
              <div className="criterion-tabs">
                {analysisState.map((analysis, index) => {
                  const completedBoundaries = analysis.gradeBoundaryAnalyses.filter(b => b.isComplete).length;
                  const totalBoundaries = analysis.gradeBoundaryAnalyses.length;
                  const isComplete = analysis.isComplete;
                  const progressPercentage = totalBoundaries > 0 ? (completedBoundaries / totalBoundaries) * 100 : 0;
                  
                  return (
                    <button
                      key={index}
                      className={`criterion-tab ${index === currentCriterionIndex ? 'active' : ''} ${isComplete ? 'sufficient' : ''}`}
                      onClick={() => {
                        setCurrentCriterionIndex(index);
                        setCurrentBoundaryIndex(0);
                      }}
                    >
                      <span className="criterion-tab__title">{analysis.criterionTitle}</span>
                      <div className="criterion-tab__progress-bar">
                        <div 
                          className="criterion-tab__progress-fill" 
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {currentQuestion ? (() => {
              const question = currentQuestion!; // Non-null assertion since we checked above
              return (
              <div className="current-criterion">
                <h4 className="current-criterion__title">{question.criterionTitle}</h4>
                
                {/* 現在の質問情報 */}
                <div className="current-criterion__descriptions">
                  <span className="criterion-description-tag">
                    {t('rubricGeneration.question.criterionLabel')} {question.criterionTitle}
                  </span>
                  <span className="criterion-description-tag">
                    {question.upperGrade}{t('rubricGeneration.question.gradeSuffix')} vs {question.lowerGrade}{t('rubricGeneration.question.gradeSuffix')}
                  </span>
                </div>

                <div className="rubric-question-content">
                  <h5 className="question-title">
                    {t('rubricGeneration.question.title', {
                      criterion: question.criterionTitle,
                      upperGrade: question.upperGrade,
                      lowerGrade: question.lowerGrade
                    })}
                  </h5>
                  
                  <div className="difference-input">
                    <textarea
                      value={currentDifference}
                      onChange={(e) => setCurrentDifference(e.target.value)}
                      placeholder={t('rubricGeneration.question.placeholder', {
                        upperGrade: question.upperGrade,
                        lowerGrade: question.lowerGrade
                      })}
                      rows={4}
                      className="difference-textarea"
                    />
                    <Button 
                      onClick={handleAddDifference}
                      disabled={!currentDifference.trim()}
                      size="sm"
                    >
                      {t('rubricGeneration.question.addButton')}
                    </Button>
                  </div>

                  {question?.boundary?.differences && question.boundary.differences.length > 0 && (
                    <div className="differences-list">
                      <h6>{t('rubricGeneration.question.recordedDifferences')}</h6>
                      {question.boundary.differences.map((diff, index) => (
                        <div key={index} className="difference-item">
                          <span className="difference-text">{diff}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveDifference(index)}
                            leftIcon={<XIcon />}
                          >
                            {t('rubricGeneration.question.deleteButton')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="question-actions">
                    <Button
                      onClick={handleCompleteBoundaryAnalysis}
                      disabled={!question?.boundary?.differences?.length}
                      leftIcon={<CheckIcon />}
                    >
                      {t('rubricGeneration.question.completeButton')}
                    </Button>
                  </div>
                </div>
              </div>
              );
            })() : null}
          </div>
        ) : (
          // ルーブリック表示フェーズ
          <div className="rubric-display">

            <div className="rubric-table-container">
              <table className="rubric-table">
                <thead>
                  <tr>
                    <th className="criterion-header">観点</th>
                    {getGrades(0).sort((a, b) => b.gradeXStart - a.gradeXStart).filter(grade => grade.gradeLetter && grade.gradeLetter.trim().length > 0).map(grade => (
                      <th key={grade.gradeLetter} className={`grade-header grade-${grade.gradeLetter.toLowerCase()}`}>
                        {grade.gradeLetter}{t('rubricGeneration.question.gradeSuffix')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rubric.map((row, criterionIndex) => (
                    <tr key={criterionIndex}>
                      <td className="criterion-cell">
                        <strong>{criteria[criterionIndex]?.title}</strong>
                      </td>
                      {row.map((cell, gradeIndex) => {
                        const isEditing = editingCell?.row === criterionIndex && editingCell?.col === gradeIndex;
                        
                        return (
                          <td 
                            key={gradeIndex} 
                            className="rubric-cell"
                          >
                            {isEditing ? (
                              <div className="rubric-cell-edit">
                                <textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => {
                                    // 編集内容を保存
                                    const newRubric = [...rubric];
                                    newRubric[criterionIndex][gradeIndex] = {
                                      ...cell,
                                      description: editValue
                                    };
                                    setRubric(newRubric);
                                    // ストアに保存（既に統一されたRubricCell型を使用）
                                    setRubricData(newRubric);
                                    setEditingCell(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      // 編集内容を保存
                                      const newRubric = [...rubric];
                                      newRubric[criterionIndex][gradeIndex] = {
                                        ...cell,
                                        description: editValue
                                      };
                                      setRubric(newRubric);
                                      // ルーブリックデータを直接ストアに保存
                                      setRubricData(newRubric);
                                      setEditingCell(null);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingCell(null);
                                    }
                                  }}
                                  autoFocus
                                  className="rubric-cell-textarea"
                                  rows={4}
                                />
                              </div>
                            ) : (
                              <div className="rubric-cell-content">
                                <div className="rubric-cell-text">
                                  {cell.description}
                                </div>
                                <button
                                  className="rubric-cell-edit-button"
                                  onClick={() => {
                                    setEditingCell({ row: criterionIndex, col: gradeIndex });
                                    setEditValue(cell.description);
                                  }}
                                  aria-label={t('rubricGeneration.display.editCellAria')}
                                >
                                  <Pencil size={16} strokeWidth={2} />
                                </button>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rubric-actions">
              <Button 
                variant="secondary" 
                onClick={() => {
                  setIsRubricGenerated(false);
                  setSkipToAutoGenerate(false);
                  setIsAnalysisComplete(false);
                }}
              >
                {t('rubricGeneration.display.regenerateButton')}
              </Button>
              {/* 編集ヒントを削除 */}
            </div>
          </div>
        )}
      </div>

      <StepNavigation
        currentStep={5}
        canProceed={canProceed}
        onNext={handleNext}
        onPrev={handlePrev}
        nextLabel={t('rubricGeneration.navigation.nextToQuick')}
        prevLabel={t('rubricGeneration.navigation.backToGrade')}
      />

      {/* 差異説明ダイアログ */}
      <DifferenceExplanationDialog
        isOpen={showDifferenceDialog}
        onClose={() => setShowDifferenceDialog(false)}
        onComplete={handleDifferenceDialogComplete}
        questions={differenceQuestions}
      />
    </section>
  );
};
